import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

import type { Lead, LeadFilters } from 'features/leadReferrals/types'
import type { RootState } from 'store/store'

export interface LeadAppliedFilterView {
  id: string
  name: string
}

export interface LeadListingState {
  searchQuery: string
  filters: LeadFilters
  // Saved view the current filters were applied from (US 16962). Persisted so
  // the dropdown selection survives a refresh; restored by identity against
  // the fetched views list, so a view deleted elsewhere degrades gracefully.
  appliedFilterView: LeadAppliedFilterView | null
  page: number
  pageSize: number
  sortValue: string
  // Archived view keeps its own search/filters/page/sort.
  archivedSearchQuery: string
  archivedFilters: LeadFilters
  archivedPage: number
  archivedPageSize: number
  archivedSortValue: string
  isQuickAddOpen: boolean
  currentLead: Lead | null
  isLeadNotesDrawerOpen: boolean
  // One-shot flag: return to Archived after a referral detour from the Archived list.
  returnToArchivedAfterReferral: boolean
}

const initialFilters: LeadFilters = {
  organizationId: null,
  communityIds: [],
  routes: [],
  sourceHospitalIds: [],
  hasDischargeOrder: null,
  createdDateFrom: null,
  createdDateTo: null,
  convertedDateFrom: null,
  convertedDateTo: null,
}

const initialState: LeadListingState = {
  searchQuery: '',
  filters: initialFilters,
  appliedFilterView: null,
  page: 1,
  pageSize: 10,
  sortValue: 'createddate desc',
  archivedSearchQuery: '',
  archivedFilters: initialFilters,
  archivedPage: 1,
  archivedPageSize: 10,
  archivedSortValue: 'converteddate desc',
  isQuickAddOpen: false,
  currentLead: null,
  isLeadNotesDrawerOpen: false,
  returnToArchivedAfterReferral: false,
}

export const leadSlice = createSlice({
  name: 'lead',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
      state.page = 1
    },
    setFilters: (state, action: PayloadAction<LeadFilters>) => {
      state.filters = action.payload
      state.page = 1
    },
    clearFilters: state => {
      state.filters = initialFilters
      state.appliedFilterView = null
      state.page = 1
    },
    setAppliedFilterView: (
      state,
      action: PayloadAction<LeadAppliedFilterView | null>,
    ) => {
      state.appliedFilterView = action.payload
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
      state.page = 1
    },
    setSortValue: (state, action: PayloadAction<string>) => {
      state.sortValue = action.payload
      state.page = 1
    },
    openQuickAdd: state => {
      state.isQuickAddOpen = true
    },
    closeQuickAdd: state => {
      state.isQuickAddOpen = false
    },
    setCurrentLead: (state, action: PayloadAction<Lead | null>) => {
      state.currentLead = action.payload
    },
    setIsLeadNotesDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isLeadNotesDrawerOpen = action.payload
    },
    setReturnToArchivedAfterReferral: (state, action: PayloadAction<boolean>) => {
      state.returnToArchivedAfterReferral = action.payload
    },
    setArchivedSearchQuery: (state, action: PayloadAction<string>) => {
      state.archivedSearchQuery = action.payload
      state.archivedPage = 1
    },
    setArchivedFilters: (state, action: PayloadAction<LeadFilters>) => {
      state.archivedFilters = action.payload
      state.archivedPage = 1
    },
    clearArchivedFilters: state => {
      state.archivedFilters = initialFilters
      state.archivedPage = 1
    },
    setArchivedPage: (state, action: PayloadAction<number>) => {
      state.archivedPage = action.payload
    },
    setArchivedPageSize: (state, action: PayloadAction<number>) => {
      state.archivedPageSize = action.payload
      state.archivedPage = 1
    },
    setArchivedSortValue: (state, action: PayloadAction<string>) => {
      state.archivedSortValue = action.payload
      state.archivedPage = 1
    },
    resetArchivedListing: state => {
      state.archivedSearchQuery = ''
      state.archivedFilters = initialFilters
      state.archivedPage = 1
      state.archivedPageSize = 10
      state.archivedSortValue = 'converteddate desc'
    },
    resetListing: () => initialState,
  },
  extraReducers: builder => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const leadActions = leadSlice.actions

export const selectLeadSearchQuery = (state: RootState) => state.lead.searchQuery
const selectRawLeadFilters = (state: RootState) => state.lead.filters
export const selectLeadFilters = createSelector(
  [selectRawLeadFilters],
  (filters): LeadFilters => ({ ...initialFilters, ...filters }),
)
// `?? null` guards rehydrated persisted state written before this field existed.
export const selectLeadAppliedFilterView = (state: RootState) =>
  state.lead.appliedFilterView ?? null
export const selectLeadPage = (state: RootState) => state.lead.page
export const selectLeadPageSize = (state: RootState) => state.lead.pageSize
export const selectLeadSortValue = (state: RootState) =>
  state.lead.sortValue ?? 'createddate desc'
export const selectIsQuickAddOpen = (state: RootState) => state.lead.isQuickAddOpen
export const selectCurrentLead = (state: RootState) => state.lead.currentLead
export const selectIsLeadNotesDrawerOpen = (state: RootState) =>
  state.lead.isLeadNotesDrawerOpen

export const selectReturnToArchivedAfterReferral = (state: RootState) =>
  state.lead.returnToArchivedAfterReferral ?? false

export const selectLeadArchivedSearchQuery = (state: RootState) =>
  state.lead.archivedSearchQuery ?? ''
const selectRawLeadArchivedFilters = (state: RootState) => state.lead.archivedFilters
export const selectLeadArchivedFilters = createSelector(
  [selectRawLeadArchivedFilters],
  (filters): LeadFilters => ({ ...initialFilters, ...(filters ?? {}) }),
)
export const selectLeadArchivedPage = (state: RootState) => state.lead.archivedPage ?? 1
export const selectLeadArchivedPageSize = (state: RootState) =>
  state.lead.archivedPageSize ?? 10
export const selectLeadArchivedSortValue = (state: RootState) =>
  state.lead.archivedSortValue ?? 'converteddate desc'

export default leadSlice.reducer
